'use client';
import React from 'react';
import Link from 'next/link';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';

export default function VoiceDataAICaseStudy() {
  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      {/* Hero */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-10" />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(43,98,239,0.06), transparent, rgb(8,11,18))' }} />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-8">
          <Link href="/case-studies" className="inline-flex items-center gap-2 text-cobalt text-sm font-medium mb-8 hover:text-cobalt-glow transition-colors">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="m15 18-6-6 6-6" />
            </svg>
            Back to Case Studies
          </Link>
          <span className="text-cobalt font-mono text-sm tracking-widest uppercase mb-4 block">AI Data</span>
          <h1 className="font-display font-bold text-4xl lg:text-5xl text-silver-text leading-tight mb-6 max-w-4xl">
            Multilingual Voice Datasets for a Leading Synthetic Speech Technology Company
          </h1>
          <p className="text-lg text-silver-muted max-w-2xl leading-relaxed mb-10">
            20-language voice dataset delivery for AI training - with auditable consent, standardized recording protocols, and training-ready quality control for a leading AI-powered text-to-speech platform.
          </p>
          <div className="flex flex-wrap gap-6">
            {[
              { value: '20', label: 'Languages' },
              { value: 'Auditable', label: 'Consent Controls' },
              { value: 'Dual-Version', label: 'Audio Delivery' },
              { value: 'Multi-Tier', label: 'QA Framework' },
            ]?.map((stat) => (
              <div key={stat?.label} className="glow-card rounded-xl px-6 py-4 text-center min-w-[120px]">
                <div className="text-xl font-bold text-cobalt-glow">{stat?.value}</div>
                <div className="text-xs text-silver-muted mt-1">{stat?.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* Content */}
      <section className="py-16 lg:py-20">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-12">
            <div className="lg:col-span-2 space-y-12">

              {/* Background */}
              <div>
                <h2 className="font-display font-bold text-2xl text-silver-text mb-5 flex items-center gap-3">
                  <span className="w-8 h-8 rounded-lg bg-cobalt/20 border border-cobalt/30 flex items-center justify-center text-cobalt-glow text-sm font-mono">01</span>
                  About the Client
                </h2>
                <p className="text-silver-muted leading-relaxed mb-4">
                  The client is a leading US AI-powered text-to-speech platform offering realistic AI voices across multiple languages and accents for multimedia content creation. To expand their multilingual voice synthesis capabilities, they required high-quality voice datasets meeting strict technical and ethical standards.
                </p>
                <p className="text-silver-muted leading-relaxed">
                  The challenge was sourcing voice talent capable of delivering pristine audio recordings with specific accent requirements and technical standards suitable for AI training - while maintaining ethical consent standards and budget constraints.
                </p>
              </div>

              {/* Technical Specifications */}
              <div>
                <h2 className="font-display font-bold text-2xl text-silver-text mb-5 flex items-center gap-3">
                  <span className="w-8 h-8 rounded-lg bg-cobalt/20 border border-cobalt/30 flex items-center justify-center text-cobalt-glow text-sm font-mono">02</span>
                  Technical Specifications
                </h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  {[
                    { title: 'Audio Quality', desc: 'High-fidelity recordings capturing full vocal frequency spectrum' },
                    { title: 'Recording Environment', desc: 'Clean acoustic conditions with no background noise or interference' },
                    { title: 'Signal Integrity', desc: 'No distortion, clipping, or compression artifacts' },
                    { title: 'Format', desc: 'Uncompressed mono WAV format for maximum fidelity' },
                    { title: 'Dual-Version Delivery', desc: 'Both RAW and PROCESSED versions submitted for each recording' },
                    { title: 'Accent Requirements', desc: 'Specific regional accent profiles per language, verified by native reviewers' },
                  ]?.map((item) => (
                    <div key={item?.title} className="glow-card rounded-xl p-5">
                      <h3 className="font-semibold text-silver-text text-sm mb-1">{item?.title}</h3>
                      <p className="text-xs text-silver-muted">{item?.desc}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Methodology */}
              <div>
                <h2 className="font-display font-bold text-2xl text-silver-text mb-5 flex items-center gap-3">
                  <span className="w-8 h-8 rounded-lg bg-cobalt/20 border border-cobalt/30 flex items-center justify-center text-cobalt-glow text-sm font-mono">03</span>
                  Into23 Methodology
                </h2>
                <div className="space-y-4">
                  {[
                    { step: '1', title: 'Talent Recruiting & Vetting', desc: 'Sourcing and screening voice talent across 20 languages, verifying accent profiles, vocal quality, and availability for ongoing recording sessions.' },
                    { step: '2', title: 'Standardized Recording Setup', desc: 'Establishing consistent technical workflows and recording environments across all language teams to ensure uniform audio quality.' },
                    { step: '3', title: 'Voice Fingerprint Process', desc: 'Into23 implemented a proprietary "voice fingerprint" recording process to ensure legal and ethical compliance for voice data usage in AI training.' },
                    { step: '4', title: 'Multi-Tier Quality Control', desc: 'Rigorous QA framework covering technical audio quality, accent accuracy, script adherence, and consent documentation.' },
                    { step: '5', title: 'Project Management & Delivery', desc: 'End-to-end project management ensuring timely delivery of training-ready audio files in both RAW and PROCESSED formats.' },
                  ]?.map((item) => (
                    <div key={item?.step} className="flex gap-4 p-5 glow-card rounded-xl">
                      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-cobalt/20 border border-cobalt/30 flex items-center justify-center text-cobalt-glow text-sm font-bold">{item?.step}</div>
                      <div>
                        <h3 className="font-semibold text-silver-text text-sm mb-1">{item?.title}</h3>
                        <p className="text-xs text-silver-muted leading-relaxed">{item?.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Challenges Addressed */}
              <div>
                <h2 className="font-display font-bold text-2xl text-silver-text mb-5 flex items-center gap-3">
                  <span className="w-8 h-8 rounded-lg bg-cobalt/20 border border-cobalt/30 flex items-center justify-center text-cobalt-glow text-sm font-mono">04</span>
                  Challenges Addressed
                </h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  {[
                    { challenge: 'Talent Availability & Retention', solution: 'Structured talent pipeline with ongoing engagement and fair compensation models' },
                    { challenge: 'Budget Optimization', solution: 'Efficient recording workflows reducing per-minute costs without compromising quality' },
                    { challenge: 'Ethical Consent Compliance', solution: 'Voice fingerprint process ensuring legal and GDPR-aligned consent for AI training use' },
                    { challenge: 'Technical Consistency', solution: 'Standardized recording protocols across all 20 languages and diverse recording environments' },
                  ]?.map((item) => (
                    <div key={item?.challenge} className="glow-card rounded-xl p-5">
                      <h3 className="font-semibold text-cobalt-glow text-xs uppercase tracking-wider mb-2">{item?.challenge}</h3>
                      <p className="text-xs text-silver-muted">{item?.solution}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Results */}
              <div>
                <h2 className="font-display font-bold text-2xl text-silver-text mb-5 flex items-center gap-3">
                  <span className="w-8 h-8 rounded-lg bg-cobalt/20 border border-cobalt/30 flex items-center justify-center text-cobalt-glow text-sm font-mono">05</span>
                  Results & Impact
                </h2>
                <div className="grid sm:grid-cols-2 gap-4 mb-6">
                  {[
                    { metric: '20 languages delivered', detail: 'High-quality voice datasets across all target languages' },
                    { metric: 'Training-ready audio', detail: 'All files meeting technical specifications for AI model training' },
                    { metric: 'Auditable consent', detail: 'Full legal compliance for voice data use in AI synthesis' },
                    { metric: 'Expanded voice library', detail: 'Client able to launch new multilingual voice synthesis capabilities' },
                  ]?.map((r) => (
                    <div key={r?.metric} className="glow-card rounded-xl p-5 border-l-2 border-cobalt/40">
                      <div className="font-semibold text-cobalt-glow text-sm mb-1">{r?.metric}</div>
                      <div className="text-xs text-silver-muted">{r?.detail}</div>
                    </div>
                  ))}
                </div>
                <div className="glow-card rounded-xl p-5 bg-cobalt/5">
                  <h3 className="font-semibold text-silver-text text-sm mb-2">Into23 as AI Data Partner</h3>
                  <p className="text-xs text-silver-muted leading-relaxed">This project marked Into23's evolution from a translation and localization company to a specialized AI data services provider - with proven methodologies and scalable frameworks for multilingual AI voice data programs.</p>
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              <div className="glow-card rounded-xl p-6">
                <h3 className="font-semibold text-silver-text mb-4">Project Details</h3>
                <dl className="space-y-3 text-sm">
                  <div><dt className="text-silver-muted text-xs uppercase tracking-wider mb-1">Client</dt><dd className="text-silver-text">US AI Text-to-Speech Platform</dd></div>
                  <div><dt className="text-silver-muted text-xs uppercase tracking-wider mb-1">Industry</dt><dd className="text-silver-text">AI / Synthetic Speech</dd></div>
                  <div><dt className="text-silver-muted text-xs uppercase tracking-wider mb-1">Languages</dt><dd className="text-silver-text">20 languages</dd></div>
                  <div><dt className="text-silver-muted text-xs uppercase tracking-wider mb-1">Audio Format</dt><dd className="text-silver-text">Uncompressed mono WAV</dd></div>
                  <div><dt className="text-silver-muted text-xs uppercase tracking-wider mb-1">Delivery</dt><dd className="text-silver-text">RAW + PROCESSED versions</dd></div>
                  <div><dt className="text-silver-muted text-xs uppercase tracking-wider mb-1">Compliance</dt><dd className="text-silver-text">GDPR-aligned consent</dd></div>
                </dl>
              </div>

              <div className="glow-card rounded-xl p-6">
                <h3 className="font-semibold text-silver-text mb-3">Key Capabilities Used</h3>
                <ul className="space-y-2 text-sm text-silver-muted">
                  {['Voice talent sourcing', 'Accent verification', 'Voice fingerprint process', 'Standardized recording protocols', 'Multi-tier audio QA', 'Consent management', 'AI training data delivery']?.map((cap) => (
                    <li key={cap} className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-cobalt-glow flex-shrink-0" />
                      {cap}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="glow-card rounded-xl p-6">
                <h3 className="font-semibold text-silver-text mb-3">Need Voice Data for AI?</h3>
                <p className="text-sm text-silver-muted mb-4">Talk to our team about multilingual voice dataset programs.</p>
                <Link href="/get-a-quote">
                  <span className="block text-center px-4 py-3 bg-cobalt hover:bg-cobalt-glow text-white text-sm font-semibold rounded-lg transition-all">
                    Get a Quote
                  </span>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
